import styled, { css, keyframes } from 'styled-components';
import { palette } from '../../../theme/colors';

const menuFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const menuFadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-4px) scale(0.98);
  }
`;

export const SContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  max-width: 22rem;
`;

export const SPillButton = styled.button<{ $expanded: boolean; $disabled: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.palette.border.primary};
  background: ${({ theme }) => theme.palette.background.secondary};
  color: ${({ theme }) => theme.palette.text.primary};
  cursor: pointer;
  transition:
    background-color 180ms ease,
    border-color 180ms ease;

  &:hover:not(:disabled),
  &:focus-visible {
    border-color: ${({ theme }) => theme.palette.text.secondary};
    outline: none;
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
      cursor: progress;
    `}
`;

export const SName = styled.span`
  flex: 1;
  min-width: 48px;
  margin-left: 10px;
  margin-right: 6px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

export const SChevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: 14px;
  flex: 0 0 auto;
  transition: transform 220ms ease;
  transform: rotate(${({ $expanded }) => ($expanded ? 180 : 0)}deg);

  svg {
    width: 13px;
    height: 8px;
  }
`;

export const SMenu = styled.div<{ $isClosing?: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  min-width: 16rem;
  background: ${({ theme }) => theme.palette.background.secondary};
  border: 1px solid ${({ theme }) => theme.palette.border.primary};
  border-radius: 10px;
  box-shadow: 0 12px 32px ${palette.shadowColor};
  padding: 0;
  overflow: hidden;
  z-index: 1000;
  transform-origin: top center;
  animation: ${({ $isClosing }) => ($isClosing ? menuFadeOut : menuFadeIn)} 160ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const SMenuHeader = styled.div`
  padding: 14px 16px 8px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${palette.grayx11gray};
`;

export const SMenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.text.primary};

  &:hover,
  &:focus-visible {
    background: ${palette.whiteWith10Opacity};
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const SMenuItemLabel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  margin-left: 0;
  margin-right: 4px;
`;

export const SMenuItemName = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SMenuItemEmail = styled.span`
  margin-top: 2px;
  font-size: 12px;
  color: ${palette.grayx11gray};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SCheckBubbleSlot = styled.span`
  width: 25px;
  min-height: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  margin-left: auto;
`;

export const SCheckBubble = styled.span<{ $checked: boolean }>`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: transparent;
  border: 1px solid ${({ $checked }) => ($checked ? palette.earthYellow : palette.ashesGray)};
  transform: scale(0.7);
  transform-origin: center;
  transition: border-color 180ms ease;

  &::after {
    content: '';
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: ${({ $checked }) => ($checked ? palette.earthYellow : 'transparent')};
    transition: background-color 180ms ease;
  }
`;

/**
 * Shrinks the shared `NotificationBubble` (sized for a 36% dashboard tile icon)
 * so it sits properly over a 24–36px tenant avatar, gives it the punched-out
 * border that matches the pill/menu background, and applies the delayed
 * bouncy entrance.
 */
export const tenantBubbleStyles = css`
  [role='notificationBubble'] {
    height: 0.75rem;
    /* Drives both icon (0.93em) and text (1em) sizing — see SBubbleWrapper. */
    font-size: 0.55rem;
    padding: 0 0.2rem;
    border: 2px solid ${({ theme }) => theme.palette.background.secondary};
    box-sizing: content-box;
    top: -2px;
    left: calc(70% - 2px);
  }
`;

/** Wrapper applied around `StackedAvatars` so the count bubble matches the avatar bubble look. */
export const SIndicatorStack = styled.span`
  display: inline-flex;
  flex: 0 0 auto;

  ${tenantBubbleStyles}

  /* Anchor the count bubble to the rightmost avatar (24px wide) instead of 70%
     of the whole stack — same visual placement as the single-avatar bubble,
     plus a small extra +2px nudge right. */
  [role='notificationBubble'] {
    left: calc(100% - 7.2px);
  }
`;

export const SDivider = styled.span`
  width: 1px;
  height: 1.5rem;
  background: ${({ theme }) => theme.palette.border.primary};
  margin-left: 6px;
  margin-right: 14px;
  flex: 0 0 auto;
`;

export const SAddOrgRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: transparent;
  border: none;
  border-top: 1px solid ${palette.whiteWith10Opacity};
  cursor: pointer;
  color: ${({ theme }) => theme.palette.text.primary};
  font-weight: 500;
  font-size: 14px;
  text-align: left;

  &:hover,
  &:focus-visible {
    background: ${palette.whiteWith10Opacity};
    outline: none;
  }
`;

export const SAddOrgIcon = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  border: 1px dashed ${({ theme }) => theme.palette.border.primary};
  color: ${palette.santasGrey};
  font-size: 18px;
  line-height: 1;
  flex: 0 0 auto;
`;
